import React, { useState } from "react";
import { Plus, Edit2, Trash2, Share2, DollarSign, ChevronDown, Check } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from "./ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";

const ListManager = ({ 
  lists, 
  currentListId, 
  onSelectList, 
  onCreateList, 
  onUpdateList, 
  onDeleteList,
  onShareList 
}) => {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingList, setEditingList] = useState(null);
  const [newListName, setNewListName] = useState("");
  const [newListColor, setNewListColor] = useState("blue");
  const [newListBudget, setNewListBudget] = useState("");

  const currentList = lists.find(list => list.id === currentListId);

  const colorOptions = [
    { name: "Blue", value: "blue", bg: "bg-blue-500", text: "text-blue-700", border: "border-blue-300" },
    { name: "Green", value: "green", bg: "bg-green-500", text: "text-green-700", border: "border-green-300" },
    { name: "Orange", value: "orange", bg: "bg-orange-500", text: "text-orange-700", border: "border-orange-300" },
    { name: "Purple", value: "purple", bg: "bg-purple-500", text: "text-purple-700", border: "border-purple-300" },
    { name: "Pink", value: "pink", bg: "bg-pink-500", text: "text-pink-700", border: "border-pink-300" },
    { name: "Red", value: "red", bg: "bg-red-500", text: "text-red-700", border: "border-red-300" },
    { name: "Indigo", value: "indigo", bg: "bg-indigo-500", text: "text-indigo-700", border: "border-indigo-300" },
    { name: "Gray", value: "gray", bg: "bg-gray-500", text: "text-gray-700", border: "border-gray-300" }
  ];

  const handleCreateList = () => {
    if (newListName.trim()) {
      const newList = {
        id: Date.now(),
        name: newListName.trim(),
        color: newListColor,
        items: [],
        budget: newListBudget ? parseFloat(newListBudget) : null,
        budgetType: "separate", // "separate" or "linked" (to profile budget)
        createdAt: new Date(),
        shared: false
      };
      
      onCreateList(newList);
      setNewListName("");
      setNewListBudget("");
      setNewListColor("blue");
      setShowCreateDialog(false);
    }
  };

  const handleEditList = () => {
    if (editingList && editingList.name.trim()) {
      onUpdateList(editingList.id, {
        name: editingList.name.trim(),
        color: editingList.color,
        budget: editingList.budget ? parseFloat(editingList.budget) : null
      });
      setShowEditDialog(false);
      setEditingList(null);
    }
  };

  const getColorConfig = (colorValue) => {
    return colorOptions.find(c => c.value === colorValue) || colorOptions[0];
  };

  return (
    <div className="flex items-center gap-2 sm:gap-3 mb-4 flex-wrap">
      {/* Current List Display & Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="outline" 
            className={`flex items-center gap-2 ${getColorConfig(currentList?.color).border} ${getColorConfig(currentList?.color).text} hover:bg-gray-50`}
          >
            <div className={`w-3 h-3 rounded-full ${getColorConfig(currentList?.color).bg}`}></div>
            <span className="font-medium">{currentList?.name || "My List"}</span>
            <Badge variant="secondary" className="ml-1 text-xs">
              {currentList?.items?.length || 0}
            </Badge>
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-64">
          <div className="p-2">
            <p className="text-sm font-medium text-gray-700 mb-2">Switch Lists</p>
            {lists.map((list) => (
              <div
                key={list.id}
                onClick={() => onSelectList(list.id)}
                className={`flex items-center justify-between p-2 rounded-md cursor-pointer hover:bg-gray-50 ${
                  list.id === currentListId ? "bg-gray-100" : ""
                }`}
              >
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${getColorConfig(list.color).bg}`}></div>
                  <span className="font-medium text-sm">{list.name}</span>
                  {list.shared && <Share2 className="h-3 w-3 text-blue-500" />}
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="text-xs">
                    {list.items?.length || 0}
                  </Badge>
                  {list.id === currentListId && <Check className="h-3 w-3 text-green-600" />}
                </div>
              </div>
            ))}
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create New List
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Quick Actions */}
      <div className="flex gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setEditingList({ ...currentList, budget: currentList.budget || "" });
            setShowEditDialog(true);
          }}
          className="text-gray-600 hover:text-gray-800"
        >
          <Edit2 className="h-4 w-4" />
        </Button>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onShareList(currentListId)}
          className="text-blue-600 hover:text-blue-800"
        >
          <Share2 className="h-4 w-4" />
        </Button>
        
        <Button
          variant="ghost"
          size="sm"
          className="text-green-600 hover:text-green-800"
        >
          <DollarSign className="h-4 w-4" />
          <span className="text-xs ml-1">
            ${currentList?.budget ? currentList.budget.toFixed(0) : "No Budget"}
          </span>
        </Button>
      </div>

      {/* Create List Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Grocery List</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">List Name</label>
              <Input
                placeholder="e.g., Weekly Shopping, Party Supplies..."
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                className="mt-1"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Color Theme</label>
              <div className="flex gap-2 mt-2 flex-wrap">
                {colorOptions.map((color) => (
                  <button
                    key={color.value}
                    onClick={() => setNewListColor(color.value)}
                    className={`w-8 h-8 rounded-full ${color.bg} ${
                      newListColor === color.value ? "ring-2 ring-gray-400" : ""
                    }`}
                  />
                ))}
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Budget (Optional)</label>
              <Input
                type="number"
                placeholder="0.00"
                value={newListBudget}
                onChange={(e) => setNewListBudget(e.target.value)}
                className="mt-1"
              />
            </div>
            
            <div className="flex gap-2">
              <Button onClick={handleCreateList} className="flex-1 bg-green-500 hover:bg-green-600">
                <Plus className="h-4 w-4 mr-2" />
                Create List
              </Button>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit List Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Grocery List</DialogTitle>
          </DialogHeader>
          {editingList && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">List Name</label>
                <Input
                  value={editingList.name}
                  onChange={(e) => setEditingList({...editingList, name: e.target.value})}
                  className="mt-1"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700">Color Theme</label>
                <div className="flex gap-2 mt-2 flex-wrap">
                  {colorOptions.map((color) => (
                    <button
                      key={color.value}
                      onClick={() => setEditingList({...editingList, color: color.value})}
                      className={`w-8 h-8 rounded-full ${color.bg} ${
                        editingList.color === color.value ? "ring-2 ring-gray-400" : ""
                      }`}
                    />
                  ))}
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700">Budget</label>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={editingList.budget}
                  onChange={(e) => setEditingList({...editingList, budget: e.target.value})}
                  className="mt-1"
                />
              </div>
              
              <div className="flex gap-2">
                <Button onClick={handleEditList} className="flex-1 bg-green-500 hover:bg-green-600">
                  Save Changes
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    if (lists.length > 1) {
                      onDeleteList(editingList.id);
                      setShowEditDialog(false);
                    }
                  }}
                  className="text-red-600 hover:text-red-800"
                  disabled={lists.length <= 1}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ListManager;